import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { ConfirmModal } from 'components/ConfirmModal';
import { Input } from 'components/Input';
import { Snackbar } from 'components/Snackbar';
import { ManagementFormLayout } from 'components/ManagementFormLayout';
import { routes } from 'constants/routes';
import { accountManagementStrings, commonStrings } from 'constants/strings';
import {
  useAdminAccount,
  useAdminOrganizations,
  useUpdateAdminAccountMembership,
  useUpdateAdminAccountProfile,
  useUpdateAdminAccountSecurity,
} from 'hooks';
import { EOrganizationMemberRole, EOrganizationMemberStatus } from 'types';
import { formatDateTime } from 'utils';
import { API_ERROR_CODE, getApiErrorCode } from 'utils/apiErrorCode';
import { toErrorMessage } from 'hooks/utils';
import { getAccountOrganizationLabel } from '../accountOrganizationDisplay';
import { AccountTermsSection } from './AccountTermsSection';

type MembershipAction = {
  organizationId: number;
  nextStatus: EOrganizationMemberStatus;
};

type MembershipOperation = 'update' | 'add';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccountManagementEdit() {
  const navigate = useNavigate();
  const strings = accountManagementStrings.edit;
  const { accountId } = useParams();
  const accountIdNumber = Number(accountId);
  const hasValidId = Number.isFinite(accountIdNumber) && accountIdNumber > 0;
  const { account, loading, error } = useAdminAccount(
    hasValidId ? accountIdNumber : undefined,
  );
  const {
    organizations,
    loading: isOrganizationsLoading,
    error: organizationsError,
  } = useAdminOrganizations();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<EOrganizationMemberRole>(
    EOrganizationMemberRole.STANDARD,
  );
  const [status, setStatus] = useState<EOrganizationMemberStatus>(
    EOrganizationMemberStatus.ACTIVE,
  );
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [validationError, setValidationError] = useState<string | undefined>();
  const [partialError, setPartialError] = useState<string | undefined>();
  const [membershipAction, setMembershipAction] =
    useState<MembershipAction | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [membershipError, setMembershipError] = useState<string | undefined>();
  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

  const {
    updateAsync: updateMembershipAsync,
    isSubmitting: isMembershipSubmitting,
  } = useUpdateAdminAccountMembership();
  const {
    updateAsync: updateProfileAsync,
    isSubmitting: isProfileUpdating,
    error: updateProfileError,
  } = useUpdateAdminAccountProfile();
  const {
    updateAsync: updateSecurityAsync,
    isSubmitting: isSecurityUpdating,
    error: updateSecurityError,
  } = useUpdateAdminAccountSecurity();

  const membershipCandidates = useMemo(() => {
    if (!account) return [];
    const membershipOrganizationIds = new Set(
      account.memberships.map((membership) => membership.organizationId),
    );
    return organizations.filter(
      (organization) => !membershipOrganizationIds.has(organization.id),
    );
  }, [account, organizations]);

  const selectedCandidateId = membershipCandidates.some(
    (organization) => String(organization.id) === selectedOrganizationId,
  )
    ? selectedOrganizationId
    : '';

  const initializedAccountIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!account) {
      initializedAccountIdRef.current = null;
      setName('');
      setEmail('');
      setRole(EOrganizationMemberRole.STANDARD);
      setStatus(EOrganizationMemberStatus.ACTIVE);
      setNewPassword('');
      setNewPasswordConfirm('');
      setValidationError(undefined);
      setPartialError(undefined);
      return;
    }
    // 所属の変更などで詳細が再取得されても、編集中の入力は上書きしない
    if (initializedAccountIdRef.current === account.id) return;
    initializedAccountIdRef.current = account.id;

    setName(account.name ?? '');
    setEmail(account.email);
    setRole(account.role);
    setStatus(account.status);
    setNewPassword('');
    setNewPasswordConfirm('');
    setValidationError(undefined);
    setPartialError(undefined);
  }, [account]);

  const isSubmitting = isProfileUpdating || isSecurityUpdating;
  const isFormDisabled = loading || !account;
  const notFound = !loading && !error && (!hasValidId || !account);
  const normalizedName = name.trim();
  const normalizedEmail = email.trim().toLowerCase();
  const hasPasswordInput = newPassword.length > 0;
  const hasProfileChanges = Boolean(
    account &&
    (normalizedName !== (account.name ?? '') ||
      role !== account.role ||
      status !== account.status),
  );
  const hasSecurityChanges = Boolean(
    account && (normalizedEmail !== account.email || hasPasswordInput),
  );
  const alertMessage =
    validationError ||
    partialError ||
    updateProfileError ||
    updateSecurityError ||
    error ||
    organizationsError ||
    (notFound ? strings.states.notFound : undefined);

  const organizationLabel =
    (account && getAccountOrganizationLabel(account)) ||
    strings.summary.emptyValue;

  const createdAt = formatDateTime(account?.createdAt, {
    fallback: strings.summary.emptyValue,
  });
  const updatedAt = formatDateTime(account?.updatedAt, {
    fallback: strings.summary.emptyValue,
  });

  const executeMembershipUpdate = async ({
    organizationId,
    status: nextStatus,
    operation,
    successMessage,
  }: {
    organizationId: number;
    status: EOrganizationMemberStatus;
    operation: MembershipOperation;
    successMessage: string;
  }) => {
    if (!account || isMembershipSubmitting) return;

    setMembershipError(undefined);
    setSnackbarMessage(undefined);
    setIsSnackbarOpen(false);

    try {
      await updateMembershipAsync({
        accountId: account.id,
        organizationId,
        request: { status: nextStatus },
      });
      setSnackbarMessage(successMessage);
      setIsSnackbarOpen(true);
    } catch (caught: unknown) {
      const errorCode = getApiErrorCode(caught);
      if (errorCode === API_ERROR_CODE.ADMIN_SINGLE_MEMBERSHIP) {
        setMembershipError(strings.memberships.errors.adminSingleMembership);
      } else if (errorCode === API_ERROR_CODE.ACTIVE_ADMIN_REQUIRED) {
        setMembershipError(strings.memberships.errors.activeAdminRequired);
      } else if (
        errorCode === API_ERROR_CODE.SELF_MEMBERSHIP_PAUSE_FORBIDDEN
      ) {
        setMembershipError(
          strings.memberships.errors.selfMembershipPauseForbidden,
        );
      } else {
        setMembershipError(
          toErrorMessage(
            caught instanceof Error ? caught : null,
            operation === 'add'
              ? strings.memberships.errors.addFailed
              : strings.memberships.errors.updateFailed,
          ),
        );
      }
    }
  };

  const handleConfirmMembershipChange = () => {
    if (!membershipAction) return;

    void executeMembershipUpdate({
      organizationId: membershipAction.organizationId,
      status: membershipAction.nextStatus,
      operation: 'update',
      successMessage: strings.memberships.messages.updateSuccess,
    }).then(() => setMembershipAction(null));
  };

  const handleAddMembership = () => {
    const organizationId = Number(selectedCandidateId);
    if (
      !account ||
      !Number.isInteger(organizationId) ||
      !membershipCandidates.some(
        (organization) => organization.id === organizationId,
      )
    ) {
      return;
    }

    void executeMembershipUpdate({
      organizationId,
      status: EOrganizationMemberStatus.ACTIVE,
      operation: 'add',
      successMessage: strings.memberships.add.success,
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isFormDisabled || isSubmitting || !account) return;

    setValidationError(undefined);
    setPartialError(undefined);

    if (!normalizedName || !normalizedEmail) {
      setValidationError(strings.errors.required);
      return;
    }

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setValidationError(strings.errors.emailInvalid);
      return;
    }

    if (hasPasswordInput && newPassword.length < 6) {
      setValidationError(strings.errors.passwordTooShort);
      return;
    }

    if (hasPasswordInput && newPassword !== newPasswordConfirm) {
      setValidationError(strings.errors.passwordConfirmMismatch);
      return;
    }

    const shouldUpdateProfile = hasProfileChanges;
    const shouldUpdateSecurity = hasSecurityChanges;

    if (!shouldUpdateProfile && !shouldUpdateSecurity) {
      return;
    }

    let hasUpdatedProfile = false;

    try {
      if (shouldUpdateProfile) {
        await updateProfileAsync(account.id, {
          name: normalizedName,
          role,
          status,
        });
        hasUpdatedProfile = true;
      }

      if (shouldUpdateSecurity) {
        const securityRequest: {
          email?: string;
          newPassword?: string;
        } = {};

        if (normalizedEmail !== account.email) {
          securityRequest.email = normalizedEmail;
        }

        if (hasPasswordInput) {
          securityRequest.newPassword = newPassword;
        }

        await updateSecurityAsync(account.id, securityRequest);
      }

      setNewPassword('');
      setNewPasswordConfirm('');
      navigate(routes.ACCOUNT_MANAGEMENT, {
        state: { snackbarMessage: strings.messages.updateSuccess },
      });
    } catch (caught: unknown) {
      // 権限・状態の変更が I6/I7 に当たるときの 409 は定型文言で理由を伝える
      const errorCode = getApiErrorCode(caught);
      if (errorCode === API_ERROR_CODE.ADMIN_SINGLE_MEMBERSHIP) {
        setValidationError(strings.errors.adminSingleMembership);
      } else if (errorCode === API_ERROR_CODE.ACTIVE_ADMIN_REQUIRED) {
        setValidationError(strings.errors.activeAdminRequired);
      } else if (hasUpdatedProfile && shouldUpdateSecurity) {
        setPartialError(strings.messages.securityOnlyFailed);
      }
    }
  };

  const isSaveDisabled =
    isFormDisabled ||
    isSubmitting ||
    (!hasProfileChanges && !hasSecurityChanges);

  return (
    <>
      <ManagementFormLayout
        kicker={strings.kicker}
        title={strings.title}
        description={strings.description}
        backTo={routes.ACCOUNT_MANAGEMENT}
      >
        {alertMessage && <Alert>{alertMessage}</Alert>}

        <section className="grid gap-3 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-ink">
            {strings.summary.title}
          </h2>
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-semibold text-muted">
                {strings.summary.labels.id}
              </dt>
              <dd className="mt-1 text-sm text-ink">
                {account?.id ?? strings.summary.emptyValue}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold text-muted">
                {strings.summary.labels.organization}
              </dt>
              <dd className="mt-1 text-sm text-ink">{organizationLabel}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold text-muted">
                {strings.summary.labels.createdAt}
              </dt>
              <dd className="mt-1 text-sm text-ink">{createdAt}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold text-muted">
                {strings.summary.labels.updatedAt}
              </dt>
              <dd className="mt-1 text-sm text-ink">{updatedAt}</dd>
            </div>
          </dl>
        </section>

        {account && <AccountTermsSection accountId={account.id} />}

        <form
          className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
          onSubmit={(event) => {
            void handleSubmit(event);
          }}
        >
          <div className="grid gap-1">
            <h2 className="text-sm font-semibold text-ink">
              {strings.form.title}
            </h2>
            <p className="text-xs text-muted">{strings.form.description}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.summary.labels.organization}
              <Input
                name="organizationName"
                value={organizationLabel}
                disabled
                readOnly
              />
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.form.fields.name.label}
              <Input
                name="name"
                placeholder={strings.form.fields.name.placeholder}
                autoComplete="name"
                required
                disabled={isFormDisabled || isSubmitting}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.form.fields.email.label}
              <Input
                name="email"
                type="email"
                placeholder={strings.form.fields.email.placeholder}
                autoComplete="email"
                required
                disabled={isFormDisabled || isSubmitting}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.form.fields.role.label}
              {account?.role === EOrganizationMemberRole.ADMIN ? (
                <Input
                  name="role"
                  value={strings.form.roleOptions.admin}
                  disabled
                  readOnly
                />
              ) : (
                <select
                  name="role"
                  className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                  disabled={isFormDisabled || isSubmitting}
                  value={role}
                  onChange={(event) =>
                    setRole(event.target.value as EOrganizationMemberRole)
                  }
                >
                  <option value={EOrganizationMemberRole.STANDARD}>
                    {strings.form.roleOptions.standard}
                  </option>
                </select>
              )}
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.form.fields.status.label}
              <select
                name="status"
                className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                disabled={isFormDisabled || isSubmitting}
                value={status}
                onChange={(event) =>
                  setStatus(event.target.value as EOrganizationMemberStatus)
                }
              >
                <option value={EOrganizationMemberStatus.ACTIVE}>
                  {strings.form.statusOptions.active}
                </option>
                <option value={EOrganizationMemberStatus.PAUSED}>
                  {strings.form.statusOptions.paused}
                </option>
              </select>
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.form.fields.newPassword.label}
              <Input
                name="newPassword"
                type="password"
                placeholder={strings.form.fields.newPassword.placeholder}
                autoComplete="new-password"
                minLength={6}
                disabled={isFormDisabled || isSubmitting}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
              <span className="text-[11px] font-medium text-muted">
                {strings.form.fields.newPassword.helper}
              </span>
            </label>
            <label className="grid gap-2 text-xs font-semibold text-ink">
              {strings.form.fields.newPasswordConfirm.label}
              <Input
                name="newPasswordConfirm"
                type="password"
                placeholder={strings.form.fields.newPasswordConfirm.placeholder}
                autoComplete="new-password"
                minLength={6}
                disabled={isFormDisabled || isSubmitting}
                value={newPasswordConfirm}
                onChange={(event) => setNewPasswordConfirm(event.target.value)}
              />
            </label>
          </div>
          <div className="flex justify-end">
            <Button size="md" type="submit" disabled={isSaveDisabled}>
              {isSubmitting
                ? `${commonStrings.actions.update}${commonStrings.loadingEllipsis}`
                : commonStrings.actions.update}
            </Button>
          </div>
        </form>

        {account && (
          <section className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
            <div className="grid gap-1">
              <h2 className="text-sm font-semibold text-ink">
                {strings.memberships.title}
              </h2>
              <p className="text-xs text-muted">
                {strings.memberships.description}
              </p>
            </div>

            {membershipError && <Alert>{membershipError}</Alert>}

            {account.memberships.length === 0 ? (
              <p className="text-sm text-muted">{strings.memberships.states.empty}</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border/70">
                <table className="min-w-full text-left text-sm">
                  <thead className="bg-surface/70 text-xs text-muted">
                    <tr>
                      <th className="px-3 py-2 font-semibold">
                        {strings.memberships.headers.organization}
                      </th>
                      <th className="px-3 py-2 font-semibold">
                        {strings.memberships.headers.status}
                      </th>
                      <th className="px-3 py-2 font-semibold">
                        {strings.memberships.headers.actions}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.memberships.map((membership) => {
                      const nextStatus =
                        membership.status === EOrganizationMemberStatus.ACTIVE
                          ? EOrganizationMemberStatus.PAUSED
                          : EOrganizationMemberStatus.ACTIVE;

                      return (
                        <tr
                          key={membership.organizationId}
                          className="border-t border-border/60"
                        >
                          <td className="px-3 py-3 text-ink">
                            {membership.organizationName ||
                              strings.summary.emptyValue}
                          </td>
                          <td className="px-3 py-3 text-ink">
                            {membership.status ===
                            EOrganizationMemberStatus.ACTIVE
                              ? strings.memberships.statuses.active
                              : strings.memberships.statuses.paused}
                          </td>
                          <td className="px-3 py-3">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              disabled={isMembershipSubmitting}
                              onClick={() =>
                                setMembershipAction({
                                  organizationId: membership.organizationId,
                                  nextStatus,
                                })
                              }
                            >
                              {membership.status ===
                              EOrganizationMemberStatus.ACTIVE
                                ? strings.memberships.actions.pause
                                : strings.memberships.actions.activate}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="grid gap-3 rounded-xl border border-border/70 bg-surface/40 p-4">
              <h3 className="text-xs font-semibold text-ink">
                {strings.memberships.add.title}
              </h3>
              {membershipCandidates.length === 0 ? (
                <p className="text-sm text-muted">
                  {strings.memberships.add.noCandidates}
                </p>
              ) : (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                  <label className="grid flex-1 gap-2 text-xs font-semibold text-ink">
                    {strings.memberships.add.organizationLabel}
                    <select
                      aria-label={strings.memberships.add.organizationLabel}
                      className="h-11 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink shadow-sm transition focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30"
                      disabled={isMembershipSubmitting || isOrganizationsLoading}
                      value={selectedCandidateId}
                      onChange={(event) =>
                        setSelectedOrganizationId(event.target.value)
                      }
                    >
                      <option value="">
                        {strings.memberships.add.placeholder}
                      </option>
                      {membershipCandidates.map((organization) => (
                        <option key={organization.id} value={organization.id}>
                          {organization.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <Button
                    type="button"
                    size="md"
                    disabled={
                      isMembershipSubmitting ||
                      isOrganizationsLoading ||
                      !selectedCandidateId
                    }
                    onClick={handleAddMembership}
                  >
                    {strings.memberships.add.submit}
                  </Button>
                </div>
              )}
            </div>
          </section>
        )}
      </ManagementFormLayout>

      <ConfirmModal
        open={membershipAction !== null}
        textTitle={
          membershipAction?.nextStatus === EOrganizationMemberStatus.PAUSED
            ? strings.memberships.pauseConfirm.title
            : strings.memberships.activateConfirm.title
        }
        textMessage={
          membershipAction?.nextStatus === EOrganizationMemberStatus.PAUSED
            ? strings.memberships.pauseConfirm.message
            : strings.memberships.activateConfirm.message
        }
        textCancel={commonStrings.cancel}
        textConfirm={
          membershipAction?.nextStatus === EOrganizationMemberStatus.PAUSED
            ? strings.memberships.pauseConfirm.confirm
            : strings.memberships.activateConfirm.confirm
        }
        isLoading={isMembershipSubmitting}
        onClose={() => setMembershipAction(null)}
        onConfirm={handleConfirmMembershipChange}
      />

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        variant="success"
        position="top-center"
        autoHideDuration={3000}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </>
  );
}
